import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { RocketLaunch as RocketLaunchIcon } from '@mui/icons-material'
import { ButtonSquare } from '@/modules/base/components/buttons'
import featureConfig, { FeatureType, FeatureTypes } from '@/modules/base/components/modal/upgrade-modal/feature-locked/feature-config'
import SubscriptionService from '@/modules/base/components/subscription/subscriptionService'
import { SubscriptionType } from '@/redux/ducks/subscription/types/details'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import lang from '@/lang'
import { isMeinBuero } from '@/utils/build-env'
import { useNavigate } from 'react-router'

const useStyles = makeStyles((theme) => ({
  featureCard: {
    border: theme.border,
    borderColor: theme.primaryBright,
    borderRadius: theme.borderRadius,
    padding: 20,
    marginTop: 20,
    marginBottom: 20,
    backgroundColor: theme.white,
  },
  featureCardHeader: {
    display: 'flex',
    marginBottom: 12,
  },
  featureCardIcon: {
    color: theme.primary,
    height: 20,
    width: 20,
    padding: 3,
    marginTop: 3,
    backgroundColor: theme.lightBackground,
    borderRadius: theme.borderRadius,
  },
  featureCardTitle: {
    fontSize: theme.fontSizes.smedium,
    fontWeight: theme.fontWeights.bold,
    color: theme.black,
    marginLeft: 15,
  },
  featureCardSubtitle: {
    fontSize: theme.fontSizes.smedium,
    color: theme.primary,
    marginBottom: 12,
    marginLeft: 15,
  },
  featureCardDescription: {
    fontSize: theme.fontSizes.default,
    color: theme.text,
    marginBottom: 20,
    lineHeight: '20px',
  },
  featureCardList: {
    listStyle: 'disc',
    marginLeft: 20,
    marginBottom: 20,
  },
  featureCardListItem: {
    fontSize: theme.fontSizes.default,
    lineHeight: '20px',
    marginBottom: 2,
  },
  featureCardListItemTitle: {
    fontWeight: theme.fontWeights.bold,
    color: theme.black,
    marginRight: 4,
  },
  featureCardListItemDescription: {
    color: theme.black,
  },
  featureCardButton: {
    marginTop: 10,
    width: 'max-content',
    height: 52,
  },
}))

interface SubscriptionResponse {
  data?: SubscriptionType[]
}

interface FeatureLockedNoticeProps {
  featureType: FeatureType
  description?: string
}

const FeatureLockedNotice: React.FC<FeatureLockedNoticeProps> = ({
  featureType,
  description,
}) => {
  const classes = useStyles()
  const feature = featureConfig[featureType]
  const navigate = useNavigate()
  const [subscription, setSubscription] = useState<SubscriptionType | null>(null)

  useEffect(() => {
    if (!feature || feature.moduleToAdd) return
    SubscriptionService.getWebSubscriptionTypes(feature.subscriptionType).then((response: SubscriptionResponse) => {
      const subscriptionItem = response.data?.find((sub) => sub.name === feature.type) || null
      if (subscriptionItem) {
        setSubscription(subscriptionItem)
      }
    })
  }, [feature])

  const price = useMemo(() => {
    if (!subscription?.prices) return null

    const yearlyPrice = subscription.prices.find((p) => p.period === 'year')
    if (yearlyPrice?.values?.[0]?.pricePerDuration) {
      return Math.round(yearlyPrice.values[0].pricePerDuration / 12 * 100) / 100
    }
    return null
  }, [subscription])

  const upgradeButtonLabel = useMemo(() => {
    if (feature?.moduleToAdd) {
      return lang.marketing.purchaseButton
    }
    if (featureType === FeatureTypes.INVITE_USER) {
      return lang.generics.upgradeNow
    }
    return lang.featureLockedModal.selectPlan(price ? formatCurrency(price) : undefined)
  }, [feature, featureType, price])

  if (!feature || (isMeinBuero() && !feature.moduleToAdd)) return null

  const handleUpgradeClick = () => {
    navigate('/shop')
  }

  return (
    <div className={classes.featureCard}>
      <div className={classes.featureCardHeader}>
        <RocketLaunchIcon className={classes.featureCardIcon} />
        <div>
          <div className={classes.featureCardTitle}>
            {feature.title}
          </div>
          {feature.subscriptionTypeLabel && (
            <div className={classes.featureCardSubtitle}>
              {feature.subscriptionTypeLabel}
            </div>
          )}
        </div>
      </div>
      <div className={classes.featureCardDescription}>
        {description ?? feature.description}
      </div>
      {feature.features && feature.features.length > 0 && (
        <ul className={classes.featureCardList}>
          {feature.features.map((featureItem, index) => (
            <li key={index} className={classes.featureCardListItem}>
              <span className={classes.featureCardListItemTitle}>{featureItem.title}</span>
              <span className={classes.featureCardListItemDescription}>{featureItem.description}</span>
            </li>
          ))}
        </ul>
      )}
      <ButtonSquare
        label={upgradeButtonLabel}
        onClick={handleUpgradeClick}
        customClass={classes.featureCardButton}
        dataQsId={`${featureType}-upgrade-button`}
      />
    </div>
  )
}

export default FeatureLockedNotice
